import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { ApiError } from '@/api/client'
import { UnsavedChangesDialog } from '@/components/UnsavedChanges'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { listDepartments } from '@/features/employees/api'
import {
  JOB_POSTING_STATUSES,
  JOB_POSTING_STATUS_LABELS,
  createJobPosting,
  listJobPostingLocations,
  updateJobPosting,
  type JobPostingRead,
} from '@/features/recruitment/api'
import { useDirtyGuard } from '@/lib/dirtyGuard'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const schema = z.object({
  title: z.string().trim().min(1, 'Enter a title').max(200),
  department_id: z.string().trim().min(1, 'Choose a department'),
  location_id: z.string().trim().max(100),
  status: z.enum(JOB_POSTING_STATUSES),
  description: z.string().trim().max(4000),
})

type FormValues = z.infer<typeof schema>

/** Create and edit in one dialog, following the `EmployeeFormDialog` shape. `closed_at`
 *  is not on this form -- the server sets it whenever `status` moves to or off `closed`. */
export function JobPostingFormDialog({
  open,
  posting,
  onClose,
}: {
  open: boolean
  posting?: JobPostingRead
  onClose: () => void
}) {
  const isEdit = posting !== undefined
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)

  const departments = useQuery({
    queryKey: ['departments', ''],
    queryFn: () => listDepartments(),
    retry: false,
    enabled: open,
  })

  const locations = useQuery({
    queryKey: ['job-posting-locations'],
    queryFn: () => listJobPostingLocations(),
    retry: false,
    enabled: open,
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: posting?.title ?? '',
      department_id: posting?.department_id ?? '',
      location_id: posting?.location_id ?? '',
      status: (posting?.status as (typeof JOB_POSTING_STATUSES)[number]) ?? 'open',
      description: posting?.description ?? '',
    },
  })

  const guard = useDirtyGuard(open && isDirty)

  const close = () => {
    guard.attempt(() => {
      setFormError(null)
      reset()
      onClose()
    })
  }

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const data = {
        title: values.title,
        department_id: values.department_id,
        location_id: values.location_id || null,
        status: values.status,
        description: values.description || null,
      }
      return posting ? updateJobPosting(posting.id, data) : createJobPosting(data)
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['job-postings'] })
      void queryClient.invalidateQueries({ queryKey: ['job-posting', saved.id] })
      toast.success(isEdit ? 'Job posting saved.' : 'Job posting opened.')
      reset()
      onClose()
    },
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError ? error.message : 'Could not reach the server. Try again.',
      )
    },
  })

  const onSubmit = handleSubmit((values) => {
    setFormError(null)
    return save.mutateAsync(values).catch(() => {
      /* surfaced in formError by onError */
    })
  })

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title={isEdit ? `Edit ${posting.title}` : 'New job posting'}
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" form="job-posting-form" loading={isSubmitting}>
              {isEdit ? 'Save changes' : 'Open posting'}
            </Button>
          </>
        }
      >
        <form id="job-posting-form" onSubmit={onSubmit} noValidate className="space-y-4">
          {formError && <Alert tone="error">{formError}</Alert>}

          <Field label="Title" error={errors.title?.message}>
            {(props) => <Input {...props} {...register('title')} autoComplete="off" />}
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Department" error={errors.department_id?.message}>
              {(props) => (
                <select
                  {...props}
                  {...register('department_id')}
                  className={SELECT_CLASS}
                  disabled={departments.isLoading}
                >
                  <option value="">Choose a department…</option>
                  {(departments.data?.data ?? []).map((department) => (
                    <option key={department.id} value={department.id}>
                      {department.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Status" error={errors.status?.message}>
              {(props) => (
                <select {...props} {...register('status')} className={SELECT_CLASS}>
                  {JOB_POSTING_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {JOB_POSTING_STATUS_LABELS[status]}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>

          <Field
            label="Location"
            error={errors.location_id?.message}
            hint="Optional -- the warehouse or store this role is based at, if any."
          >
            {(props) => (
              <select
                {...props}
                {...register('location_id')}
                className={SELECT_CLASS}
                disabled={locations.isLoading}
              >
                <option value="">No location</option>
                {(locations.data ?? []).map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name} ({location.code})
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label="Description" error={errors.description?.message} hint="Optional.">
            {(props) => <Input {...props} {...register('description')} autoComplete="off" />}
          </Field>
        </form>
      </Modal>

      <UnsavedChangesDialog guard={guard} />
    </>
  )
}
